import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { useSelectedRows, useSelectedRowsDispatch, useModalContext } from 'common/contexts';
import BulkActionsDropdown from 'components/common/EntityDataTable/BulkActionsDropdown';
import { MenuItem, Button } from 'components/bootstrap';
import PacksModals from 'security-app/components/common/ThreatCoverageWidget/ImproveCoverageModal/PacksModals';
import usePermissions from 'hooks/usePermissions';

import ClearSelection from './clear-selection';
import ExpandedPackDetails from './expanded-pack-details';

import type { PackType } from '../types';

const TableBulkActions = () => {
  const { setModal, setEntity } = useModalContext();
  const selectedPacks = useSelectedRows();
  const onDisable = () => {
    setEntity('disable');
    setModal('CONFIRM_DISABLE_PACKS');
  };

  const onEnable = () => {
    setEntity('enable');
    setModal('CONFIRM_ENABLE_PACKS');
  };

  return (
    <>
      <BulkActionsDropdown bsSize="xs">
        <MenuItem onSelect={onEnable} disabled={selectedPacks.length < 1}>
          Enable Packs
        </MenuItem>
        <MenuItem onSelect={onDisable} disabled={selectedPacks.length < 1}>
          Disable Packs
        </MenuItem>
      </BulkActionsDropdown>
      <PacksModals />
    </>
  );
};

const PackAction = ({ pack }: { pack: PackType }) => {
  const selectedPacksDispatch = useSelectedRowsDispatch();
  const { setModal, setEntity } = useModalContext();
  const addPackToSelectedRows = useCallback(
    () => selectedPacksDispatch({ type: 'upsert', payload: [pack] }),
    [pack, selectedPacksDispatch],
  );

  const onDisable = () => {
    addPackToSelectedRows();
    setEntity('disable');
    setModal('CONFIRM_DISABLE_SINGLE_PACK');
  };

  const onEnable = () => {
    addPackToSelectedRows();
    setEntity('enable');
    setModal('CONFIRM_ENABLE_SINGLE_PACK');
  };

  return pack.enabled ? (
    <Button bsSize="xsmall" onClick={onDisable} bsStyle="danger">
      Disable
    </Button>
  ) : (
    <Button bsSize="xsmall" onClick={onEnable}>
      Enable
    </Button>
  );
};

function useTableElements(withTableBulkActions: boolean = false) {
  const selectedPacks = useSelectedRows();
  const { isPermitted } = usePermissions();
  const isPermittedToDoActions = useMemo(() => isPermitted('illuminate_bundle_management:install'), [isPermitted]);
  const selectedPacksDispatch = useSelectedRowsDispatch();

  const renderExpandedRow = React.useCallback((pack: PackType) => <ExpandedPackDetails pack={pack} />, []);

  const expandedSections = React.useMemo(
    () => ({
      restFieldsExpandedSection: {
        title: null,
        content: renderExpandedRow,
      },
    }),
    [renderExpandedRow],
  );

  const bulkSelection = React.useMemo(
    () => ({
      onChangeSelection: (packIds: Array<string>, list: Array<PackType>) => {
        const auxSelectedPacks =
          packIds.length > 0
            ? packIds.map((packId: string) => [...selectedPacks, ...list].find((pack: PackType) => pack.id === packId))
            : [];
        selectedPacksDispatch({ type: 'upsert', payload: auxSelectedPacks });
      },
      actions: withTableBulkActions ? <TableBulkActions /> : <ClearSelection />,
      isEntitySelectable: (pack: PackType) => pack.is_compatible && pack.included_in_bundle,
    }),
    [selectedPacks, selectedPacksDispatch, withTableBulkActions],
  );

  const entityActions = useCallback((pack: PackType) => <PackAction pack={pack} />, []);

  if (!isPermittedToDoActions)
    return {
      expandedSections,
    };

  return {
    expandedSections,
    bulkSelection,
    bulkActions: <ClearSelection />,
    entityActions,
  };
}

export default useTableElements;
